import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx'
import { saveAs } from 'file-saver'
import { marked } from 'marked'
import type { Scan, TimeFrame } from '@/lib/types'
import type { BobAnalyse } from '@/lib/services/bobAnalyseService'

// Configure marked for safe HTML output
marked.setOptions({
  breaks: true,
  gfm: true,
})

// Utility function to format timeframe for display
const formatTimeFrame = (timeFrame: TimeFrame): string => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  return `${formatDate(timeFrame.from)} - ${formatDate(timeFrame.to)}`
}

// Utility function to convert markdown to HTML
const markdownToHtml = (markdown: string): string => {
  return marked.parse(markdown) as string
}

export const exportScanToWord = async (
  scan: Scan,
  blockContent: Record<string, string>
): Promise<void> => {
  try {
    // Create document sections
    const docSections = []

    // Header section with scan metadata
    const headerParagraphs = [
      new Paragraph({
        children: [
          new TextRun({
            text: `${scan.title} - ${scan.location}`,
            bold: true,
            size: 32, // 16pt
          }),
        ],
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: `Status: ${scan.status === 'confirmed' ? 'Bevestigd' : scan.status === 'ready' ? 'Klaar' : 'Concept'}`,
            italics: true,
          }),
        ],
        alignment: AlignmentType.CENTER,
      }),
    ]

    // Add creator and timeframe info
    if (scan.createdBy) {
      headerParagraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Aangemaakt door: ${scan.createdBy.displayName || `${scan.createdBy.firstName} ${scan.createdBy.lastName}`}`,
            }),
          ],
        })
      )
    }

    headerParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Tijdsperiode: ${formatTimeFrame(scan.timeFrame)}`,
          }),
        ],
      })
    )

    // Add metrics
    headerParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Totaal berichten: ${scan.totalMessages}`,
            bold: true,
          }),
        ],
      })
    )

    headerParagraphs.push(new Paragraph({ children: [new TextRun({ text: '' })] })) // Empty line

    docSections.push(...headerParagraphs)

    // Add each block as a section
    scan.blocks.forEach((block, index) => {
      // Block title
      docSections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: block.title,
              bold: true,
              size: 28, // 14pt
            }),
          ],
          heading: HeadingLevel.HEADING_1,
        })
      )

      // Block content - convert HTML to plain text for Word
      const htmlContent = blockContent[block.id] || markdownToHtml(block.content)
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      const plainText = tempDiv.textContent || tempDiv.innerText || ''

      if (plainText.trim()) {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: plainText,
              }),
            ],
          })
        )
      }

      // Add sources if available
      if (block.sources && block.sources.length > 0) {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Bronnen: ${block.sources.length} bron(nen)`,
                italics: true,
                size: 20, // 10pt
              }),
            ],
          })
        )
      }

      // Add spacing between blocks (except for the last one)
      if (index < scan.blocks.length - 1) {
        docSections.push(new Paragraph({ children: [new TextRun({ text: '' })] }))
      }
    })

    // Create the document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: docSections,
        },
      ],
    })

    // Generate and download the file
    const blob = await Packer.toBlob(doc)
    // Format date as dd_mm_yyyy_HH_MM
    const fromDate = scan.timeFrame.from
    const pad = (n: number) => n.toString().padStart(2, '0')
    const formattedDate = `${pad(fromDate.getDate())}_${pad(fromDate.getMonth() + 1)}_${fromDate.getFullYear()}_${pad(fromDate.getHours())}_${pad(fromDate.getMinutes())}`
    const fileName = `Omgevingsanalyse_${scan.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${scan.location.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${formattedDate}.docx`
    saveAs(blob, fileName)
  } catch (error) {
    console.error('Error exporting to Word:', error)
    throw error // Re-throw to allow caller to handle
  }
}

export const exportBobAnalyseToWord = async (bobAnalyse: BobAnalyse): Promise<void> => {
  try {
    // Create document sections
    const docSections = []

    // Header section with BOB analysis metadata
    const headerParagraphs = [
      new Paragraph({
        children: [
          new TextRun({
            text: 'BOB-Analyse (Beeldvorming, Oordeelsvorming, Besluitvorming)',
            bold: true,
            size: 32, // 16pt
          }),
        ],
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: `Status: ${bobAnalyse.status === 'completed' ? 'Voltooid' : bobAnalyse.status === 'processing' ? 'Wordt verwerkt' : 'Concept'}`,
            italics: true,
          }),
        ],
        alignment: AlignmentType.CENTER,
      }),
    ]

    headerParagraphs.push(new Paragraph({ children: [new TextRun({ text: '' })] })) // Empty line

    docSections.push(...headerParagraphs)

    // Helper function to add section
    const addSection = (title: string, data: Record<string, string[]> | undefined) => {
      if (!data) return

      docSections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: title,
              bold: true,
              size: 28, // 14pt
            }),
          ],
          heading: HeadingLevel.HEADING_1,
        })
      )

      Object.entries(data).forEach(([category, items]) => {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${category}:`,
                bold: true,
              }),
            ],
          })
        )

        if (Array.isArray(items) && items.length > 0) {
          items.forEach(item => {
            docSections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• ${item}`,
                  }),
                ],
                indent: { left: 720 }, // 0.5 inch indent for bullets
              })
            )
          })
        }

        docSections.push(new Paragraph({ children: [new TextRun({ text: '' })] })) // Empty line after category
      })

      docSections.push(new Paragraph({ children: [new TextRun({ text: '' })] })) // Empty line between sections
    }

    // Add all three sections
    addSection('Beeldvorming', bobAnalyse.beeldvorming ?? undefined)
    addSection('Oordeelsvorming', bobAnalyse.oordeelsvorming ?? undefined)
    addSection('Besluitvorming', bobAnalyse.besluitvorming ?? undefined)

    // Create the document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: docSections,
        },
      ],
    })

    // Generate and download the file
    const blob = await Packer.toBlob(doc)
    const pad = (n: number) => n.toString().padStart(2, '0')
    const now = new Date()
    const formattedDate = `${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}`
    const fileName = `BOB_Analyse_${formattedDate}.docx`
    saveAs(blob, fileName)
  } catch (error) {
    console.error('Error exporting BOB analysis to Word:', error)
    throw error // Re-throw to allow caller to handle
  }
}
